import { type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentHYCSEmploymentData, useSubmitEnrollmentHYCSEmploymentData } from '@egw/api'
import { Button, Alert, AlertType, GenericError, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { EmploymentStatus } from '@egw/api/src/models/enrollment/enrollment'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import {
  EMPLOYMENT_STATUS,
  employmentStatusItems,
  occupationItems,
} from '../../../constants/confirm-personal-info/confirm-personal-info'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - Employment')

interface FormData {
  employmentStatus: string
  occupation: string
}

export const EmploymentPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentHYCSEmploymentData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSEmploymentData()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      employmentStatus: '',
      occupation: '',
    },
  })

  const isEmployedOrIndependent =
    (watch(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.Employed ||
    (watch(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.Independent

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          employmentStatus: formData.employmentStatus,
          hasOccupation: isEmployedOrIndependent,
          currentOccupation: formData.occupation,
        })
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMPLOYMENT_SUBMIT, {
          applicationId,
          employmentStatus: formData.employmentStatus,
        })

        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMPLOYMENT_VERIFIED, {
            applicationId,
            employmentStatus: formData.employmentStatus,
          })
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.employment.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            <PageHeader title={t('hycs.employment.title')} className="mb-xl" />
            <p className="typo-primary-base-semibold mb-md">{t('hycs.employment.employment_status_title')}</p>
            <Controller
              name="employmentStatus"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  onChange={(e) => {
                    restField.onChange(e)
                  }}
                  options={employmentStatusItems.map((item) => ({
                    value: item.value,
                    label: t(`hycs.employment.employment_status.${item.value}`),
                  }))}
                  name="employmentStatus"
                  className="pb-xl"
                  testId="employment-status-radio"
                />
              )}
            />

            {isEmployedOrIndependent ? (
              <>
                <p className="typo-primary-base-semibold mb-md">{t('hycs.employment.occupation_title')}</p>
                <Controller
                  name="occupation"
                  control={control}
                  rules={{
                    required: isEmployedOrIndependent,
                  }}
                  render={({ field: { ref, ...restField } }) => (
                    <RadioGroup
                      {...restField}
                      onChange={(e) => {
                        restField.onChange(e)
                      }}
                      options={occupationItems.map((item) => ({
                        value: item.value,
                        label: t(`hycs.employment.occupation.${item.value}`),
                      }))}
                      name="occupation"
                      className="pb-sm"
                      testId="occupation-radio"
                    />
                  )}
                />
              </>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
